import type { Lead } from "./types";

export type GpuPlatform = "B300" | "H200";

export interface ServerSku {
  platform: GpuPlatform[];
  brand: string;
  sku: string;
  aliases?: string[];
}

// B300 以用户提供的官方型号清单为主；H200 收录常见公开机型。
// 目录只负责标准化，不补写原文没有的价格、数量或配置。
export const SERVER_SKU_CATALOG: ServerSku[] = [
  { platform: ["B300"], brand: "超微", sku: "SYS-822GS-NB3RT" },
  { platform: ["B300"], brand: "超微", sku: "AS-8126GS-NB3RT" },
  { platform: ["B300"], brand: "超微", sku: "SYS-422GS-NB3RT-LCC", aliases: ["SYS-422GS-NB3RT-ALC"] },
  { platform: ["B300"], brand: "超微", sku: "AS-4126GS-NB3RT-LCC" },
  { platform: ["B300"], brand: "超微", sku: "Supermicro 2-OU HGX B300", aliases: ["2-OU HGX B300"] },
  { platform: ["B300"], brand: "Dell", sku: "PowerEdge XE9780", aliases: ["XE9780"] },
  { platform: ["B300"], brand: "Dell", sku: "PowerEdge XE9780L", aliases: ["XE9780L"] },
  { platform: ["B300"], brand: "Dell", sku: "PowerEdge XE9780LAP", aliases: ["XE9780LAP"] },
  { platform: ["B300"], brand: "Dell", sku: "PowerEdge XE9785", aliases: ["XE9785"] },
  { platform: ["B300"], brand: "Dell", sku: "PowerEdge XE9785L", aliases: ["XE9785L"] },
  { platform: ["B300"], brand: "技嘉", sku: "G894-SD3-AAX7" },
  { platform: ["B300"], brand: "技嘉", sku: "G894-AD3-AAX7" },
  { platform: ["B300"], brand: "技嘉", sku: "G894-ZD3-AAX7" },
  { platform: ["B300"], brand: "Lenovo", sku: "ThinkSystem SR680a V4", aliases: ["SR680a V4"] },
  { platform: ["B300"], brand: "HPE", sku: "ProLiant Compute XD690", aliases: ["XD690"] },
  { platform: ["B300", "H200"], brand: "HPE", sku: "ProLiant Compute XD685", aliases: ["XD685"] },
  { platform: ["B300"], brand: "ASUS", sku: "ESC NB8-E11", aliases: ["NB8-E11"] },
  { platform: ["B300"], brand: "ASUS", sku: "XA NB3I-E12", aliases: ["NB3I-E12"] },
  { platform: ["B300"], brand: "ASRock Rack", sku: "4U16X-GNR2" },
  { platform: ["B300"], brand: "ASRock Rack", sku: "8U8X" },
  { platform: ["B300"], brand: "QCT", sku: "QuantaGrid D75H-10U", aliases: ["D75H-10U"] },
  { platform: ["B300"], brand: "QCT", sku: "QuantaGrid D75L-3U", aliases: ["D75L-3U"] },
  { platform: ["B300"], brand: "NVIDIA", sku: "DGX B300" },
  { platform: ["H200"], brand: "超微", sku: "SYS-821GE-TNHR" },
  { platform: ["H200"], brand: "超微", sku: "AS-8125GS-TNHR" },
  { platform: ["H200"], brand: "Dell", sku: "PowerEdge XE9680", aliases: ["XE9680"] },
  { platform: ["H200"], brand: "技嘉", sku: "G593-SD2" },
  { platform: ["H200"], brand: "Lenovo", sku: "ThinkSystem SR680a V3", aliases: ["SR680a V3"] },
  { platform: ["H200"], brand: "HPE", sku: "Cray XD670", aliases: ["XD670"] },
  { platform: ["H200"], brand: "NVIDIA", sku: "DGX H200" },
];

export const SERVER_SKU_PROMPT = SERVER_SKU_CATALOG
  .map((entry) => `${entry.platform.join("/")} · ${entry.brand} · ${entry.sku}`)
  .join("；");

export type HardwareFields = Pick<Lead, "brand" | "sku" | "model" | "configuration" | "memory_capacity"> & Partial<Pick<Lead, "raw_text">>;

function compactModel(value: string): string {
  return value.toUpperCase().replace(/[\s_–—-]/g, "");
}

export function detectGpuPlatform(...values: Array<string | null | undefined>): GpuPlatform | null {
  const text = values.filter(Boolean).join(" ");
  if (/\bB300\b/i.test(text)) return "B300";
  if (/\bH200\b/i.test(text)) return "H200";
  return null;
}

export function findOfficialServerSku(...values: Array<string | null | undefined>): ServerSku | null {
  const candidates = SERVER_SKU_CATALOG.flatMap((entry) => [entry, ...(entry.aliases ?? []).map(() => entry)]);
  const haystacks = values.filter((value): value is string => Boolean(value)).map(compactModel);
  let best: { entry: ServerSku; length: number } | null = null;
  for (const entry of candidates) {
    for (const alias of [entry.sku, ...(entry.aliases ?? [])]) {
      const normalizedAlias = compactModel(alias);
      if (!haystacks.some((text) => text.includes(normalizedAlias))) continue;
      if (!best || normalizedAlias.length > best.length) best = { entry, length: normalizedAlias.length };
    }
  }
  return best?.entry ?? null;
}

function cleanConfiguration(value: string, platform: GpuPlatform, memory: string): string {
  let result = value
    .replace(new RegExp(`(?:HGX|DGX)?\\s*${platform}`, "gi"), "")
    .replace(/\b8\s*(?:卡|GPU)s?\b/gi, "")
    .replace(/\b\d+\s*U\b/gi, "")
    .replace(/原厂|整机/g, "");
  if (memory) result = result.replace(new RegExp(`${memory}\\s*版本`, "gi"), "");
  return result
    .replace(/[\s]*[·•|/，,；;][\s]*/g, " · ")
    .replace(/(?:\s*·\s*){2,}/g, " · ")
    .replace(/^(?:\s*·\s*)+|(?:\s*·\s*)+$/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

export function normalizeGpuHardware(fields: HardwareFields): HardwareFields {
  const original = {
    brand: fields.brand.trim(),
    sku: fields.sku.trim(),
    model: fields.model.trim(),
    configuration: fields.configuration.trim(),
    memory_capacity: fields.memory_capacity.trim(),
    ...(fields.raw_text === undefined ? {} : { raw_text: fields.raw_text }),
  };
  const official = findOfficialServerSku(fields.sku, fields.model, fields.configuration, fields.raw_text);
  const detected = detectGpuPlatform(fields.model, fields.configuration, fields.raw_text, fields.sku);
  const platform = detected ?? (official?.platform.length === 1 ? official.platform[0] : null);
  if (!platform) return original;

  const memory = fields.memory_capacity.replace(/\s+/g, "").toUpperCase();
  return {
    ...original,
    brand: original.brand || official?.brand || "",
    sku: official?.sku ?? "",
    model: `${platform}${memory ? ` ${memory}` : ""}`,
    configuration: cleanConfiguration(fields.configuration, platform, memory),
    memory_capacity: memory,
  };
}
